import { useEffect } from 'react';

import get from 'lodash/get';
import PropTypes from 'prop-types';

import AsyncTableComponent from '../../Table/AsyncTableContainer';

import {
  formatDate,
  formatRole,
  replaceUnderscoreWithSpace,
  toSentenceCase,
} from '../../../utils';
import { getFullName, getRoleName } from '../../Person/utils';
import { getExportTitle } from '../../ViewExport/utils';
import { getPropertyName } from '../utils';

import { TABLE_CONSTANTS, TABLE_ID } from '../../Table/constants';

import { DATE_RANGES_TYPES, TABLE_DATA_TYPES } from '../../../staticData';
import CellPersonWithImage from '../../Table/CellPersonWithImage';
import {
  actionsLogNamesFilter,
  buildingFilter,
  limitFilter,
  pmcFilter,
  unitFilter,
} from '../../Table/filters';
import Tooltip from '../../Tooltip';
import ActionsLogExport from './ActionsLogExport';

const ActionsLogComponent = (props) => {
  const {
    onLoad,
    items,
    getItems,
    getUnits,
    listParams,
    listKey,
    units,
    buildings,
    buildingsOptions,
    pmcs,
    actionsLogNames,
    roles,
    onExport,
  } = props;
  const columns = [
    {
      Header: 'Event/Action Name',
      id: 'action', //-
      minWidth: 200,
      sortable: false,
      accessor: (entry, isExport) =>
        isExport ? (
          `${toSentenceCase(replaceUnderscoreWithSpace(entry.action))}.\n${
            entry.description
          }`
        ) : (
          <Tooltip tooltip={entry.description}>
            {replaceUnderscoreWithSpace(entry.action)}
          </Tooltip>
        ),
    },
    {
      Header: 'Action Time',
      id: 'created_at',
      width: 180,
      headerClassName: 'sort',
      defaultSortDesc: true,
      accessor: (entry) => formatDate(entry.createdAt),
    },
    {
      Header: 'Name',
      id: 'person',
      sortable: false,
      width: 200,
      accessor: ({ person, personRole }, isExport) => {
        if (!person) return 'Unavailable';
        const { id, isAdmin, isService } = person;
        const hasActiveUrl = personRole || (isAdmin && !isService);
        if (isExport) return getFullName(person);
        return (
          <CellPersonWithImage
            person={person}
            linkUrl={
              hasActiveUrl
                ? {
                    pathname: `/carson-activity/${id}`,
                    state: { person },
                  }
                : null
            }
          />
        );
      },
    },
    {
      Header: 'PMC',
      id: 'pmc',
      sortable: false,
      accessor: (entry, isExport) =>
        getPropertyName(entry, buildings, pmcs, 'pmc', isExport),
    },
    {
      Header: 'Building',
      id: 'property',
      sortable: false,
      accessor: (entry, isExport) =>
        getPropertyName(entry, buildings, pmcs, 'building', isExport),
    },
    {
      Header: 'Role',
      id: 'Role',
      sortable: false,
      accessor: (entry) => {
        const role = get(entry, 'personRole.role', null);
        return formatRole(getRoleName(role, roles)) || 'N/R';
      },
    },
    {
      Header: 'Unit',
      id: 'unit',
      sortable: false,
      width: 80,
      accessor: (entry, isExport) =>
        getPropertyName(entry, buildings, pmcs, 'unit', isExport),
    },
    {
      Header: 'Door Name',
      id: 'Door_name',
      sortable: false,
      accessor: (entry, isExport) =>
        getPropertyName(entry, buildings, pmcs, 'door', isExport),
    },
    {
      Header: 'Device Type',
      id: 'Device Type',
      sortable: false,
      width: 100,
      accessor: (entry) => get(entry, 'deviceType') || 'web',
    },
    {
      Header: 'City',
      id: 'City',
      sortable: false,
      width: 150,
      accessor: (entry) => get(entry, 'property.propertyAddress.city') || 'N/R',
    },
  ];

  const filters = [
    pmcFilter(listParams, pmcs, { building: null, unit: null }),
    buildingFilter(listParams, buildingsOptions, { unit: null }),
    actionsLogNamesFilter(listParams, actionsLogNames),
    limitFilter(listParams),
  ];

  const selectedBuildingId = listParams?.building;

  useEffect(() => {
    selectedBuildingId && getUnits(selectedBuildingId);
  }, [selectedBuildingId]);

  if (selectedBuildingId) {
    filters.splice(2, 0, unitFilter(listParams, units));
  }

  const defaultParams = {
    offset: '0',
    building: null,
    q: null,
    action_name: null,
    created_at_after: null,
    created_at_before: null,
  };
  const action = null;
  const fieldsForBackendSort = [TABLE_CONSTANTS.created_at];
  const tableDataType = TABLE_DATA_TYPES.carsonActivity;

  const title = 'Carson Activity';
  const exportTitle = getExportTitle(title);
  const exportActions = (
    <ActionsLogExport
      title={exportTitle}
      getDataForExport={onExport}
      columns={columns}
    />
  );

  return (
    <AsyncTableComponent
      tableId={TABLE_ID.actionsLogTable}
      title={title}
      columns={columns}
      defaultSort={TABLE_CONSTANTS.created_at}
      fieldsForBackendSort={fieldsForBackendSort}
      filters={filters}
      showFiltersAreApplied={false}
      onLoad={onLoad}
      items={items}
      getItems={getItems}
      listParams={listParams}
      defaultParams={defaultParams}
      tableDataType={tableDataType}
      listKey={listKey}
      action={action}
      dateRangeType={DATE_RANGES_TYPES.created_at}
      datePicker
      manual
      defaultSortDesc
      exportActions={exportActions}
      withTimePicker
    />
  );
};

ActionsLogComponent.propTypes = {
  units: PropTypes.instanceOf(Object).isRequired,
  buildings: PropTypes.instanceOf(Object).isRequired,
  pmcs: PropTypes.instanceOf(Object).isRequired,
  onLoad: PropTypes.func.isRequired,
  getItems: PropTypes.func.isRequired,
  getUnits: PropTypes.func.isRequired,
  items: PropTypes.instanceOf(Object).isRequired,
  listParams: PropTypes.instanceOf(Object).isRequired,
  listKey: PropTypes.string.isRequired,
  actionsLogNames: PropTypes.instanceOf(Array).isRequired,
  roles: PropTypes.instanceOf(Array).isRequired,
  buildingsOptions: PropTypes.instanceOf(Object).isRequired,
  onExport: PropTypes.func.isRequired,
};

export default ActionsLogComponent;
